import { ButtonVariants, type Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import IconChevronLeft from "central-icons/IconChevronLeft";
import IconChevronRight from "central-icons/IconChevronRight";
import IconDotGrid1x3Horizontal from "central-icons/IconDotGrid1x3Horizontal";
import type * as React from "react";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
    return (
        <nav
            role="navigation"
            aria-label="pagination"
            data-slot="pagination"
            className={cn("mx-auto flex w-full justify-center", className)}
            {...props}
        />
    );
}

function PaginationContent({ className, ...props }: React.ComponentProps<"ul">) {
    return (
        <ul data-slot="pagination-content" className={cn("flex flex-row items-center gap-1", className)} {...props} />
    );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
    return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
    isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, "size"> &
    React.ComponentProps<"a">;

function PaginationLink({ className, isActive, size = "sm", ...props }: PaginationLinkProps) {
    return (
        <a
            aria-current={isActive ? "page" : undefined}
            data-slot="pagination-link"
            data-active={isActive}
            className={cn(
                ButtonVariants({
                    variant: isActive ? "outline" : "ghost",
                    size,
                }),
                "tabular-nums",
                { "border border-transparent": !isActive }, // compensate for spacing differences between outline and ghost variants
                { "border-brand-foreground bg-surface-brand-subtle": isActive },
                className,
            )}
            {...props}
        />
    );
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
    return (
        <PaginationLink
            aria-label="Go to previous page"
            size="md"
            className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
            {...props}
        >
            <IconChevronLeft className="size-4" />
            <span className="sr-only">Previous</span>
        </PaginationLink>
    );
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
    return (
        <PaginationLink
            aria-label="Go to next page"
            size="md"
            className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
            {...props}
        >
            <span className="sr-only">Next</span>
            <IconChevronRight className="size-4" />
        </PaginationLink>
    );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            aria-hidden
            data-slot="pagination-ellipsis"
            className={cn("flex size-6 items-end justify-center", className)}
            {...props}
        >
            <IconDotGrid1x3Horizontal className="size-4" />
            <span className="sr-only">More pages</span>
        </span>
    );
}

export {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
};
